import { NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { DocumentChunk } from '@reflact/docsearch';
import { ToastrService } from '@shared-angular/services/toastr.service';
import dayjs from 'dayjs';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Button, ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { FileUpload, FileUploadErrorEvent, FileUploadModule } from "primeng/fileupload";
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { Popover } from "primeng/popover";
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { TreeModule } from 'primeng/tree';
import { ProjectSubmenuComponent } from "../../../../../../project-submenu/project-submenu.component";
import { ExtractionDialogComponent } from '../../../../../../shared/components/extraction-dialog/extraction-dialog.component';
import { runBotBatch } from '../../../../../../shared/services/bot-bulk-actions';
import { BulkAction, BulkActionBarComponent } from '../../../../../../shared/components/bulk-action-bar/bulk-action-bar.component';
import { FileUploadService, SelectableDocument } from '../../../../../../shared/services/file-upload.service';
import { ProjectDocumentsService } from '../../../../../../shared/services/project-documents.service';
import { ProjectService } from '../../../../../../shared/services/project.service';
import { SharePointService } from '../../../../../../shared/services/sharepoint.service';
import { ProjectDocumentDetailComponent } from './project-document-detail/project-document-detail.component';
import { SharepointButtonComponent } from "./sharepoint-button/sharepoint-button";
import { WebsiteButtonComponent } from './website-button/website-button';

@Component({
  selector: 'app-project-documents-tab',
  imports: [RouterLink, NgTemplateOutlet, ProjectSubmenuComponent,
    ConfirmPopupModule,
    ButtonModule,
    TableModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    FileUploadModule,
    SharepointButtonComponent,
    WebsiteButtonComponent,
    TooltipModule,
    Popover,
    ConfirmDialogModule,
    DialogModule,
    TagModule,
    TreeModule,
    SelectModule,
    MultiSelectModule,
    ToggleSwitchModule,
    InputNumberModule,
    FormsModule,
    DrawerModule,
    ProjectDocumentDetailComponent,
    ExtractionDialogComponent,
    BulkActionBarComponent
  ],
  providers: [ConfirmationService],
  standalone: true,
  templateUrl: './project-documents-tab.component.html',
  styleUrl: './project-documents-tab.component.scss'
})
export class ProjectDocumentsTabComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  public get bulkActions(): BulkAction[] {
    return [
      {
        id: 'bulk-delete',
        label: $localize`:@@bulk-delete:Löschen`,
        icon: 'ri-delete-bin-line',
        disabled: this.bulkBusy,
        command: () => this.confirmBulkDelete()
      }
    ];
  }

  @ViewChild('fileReplace') public fileReplaceComponent!: FileUpload;
  @ViewChild(SharepointButtonComponent) private sharepointBtn?: SharepointButtonComponent;
  @ViewChild(WebsiteButtonComponent) private websiteBtn?: WebsiteButtonComponent;

  private activePop: Popover | null = null;
  // Folgende Felder sind signal-backed, weil sie per Subscription bzw. nach await gesetzt werden.
  private readonly _projectDocuments = signal<SelectableDocument[]>([]);
  public get projectDocuments(): SelectableDocument[] { return this._projectDocuments(); }
  public set projectDocuments(value: SelectableDocument[]) { this._projectDocuments.set(value); }
  private readonly _selectedDocuments = signal<SelectableDocument[]>([]);
  public get selectedDocuments(): SelectableDocument[] { return this._selectedDocuments(); }
  public set selectedDocuments(value: SelectableDocument[]) { this._selectedDocuments.set(value); }
  private readonly _bulkBusy = signal(false);
  public get bulkBusy(): boolean { return this._bulkBusy(); }
  public set bulkBusy(value: boolean) { this._bulkBusy.set(value); }
  private readonly _bulkFailedNames = signal('');
  public get bulkFailedNames(): string { return this._bulkFailedNames(); }
  public set bulkFailedNames(value: string) { this._bulkFailedNames.set(value); }
  public dayjs = dayjs;
  public contextMenu: (MenuItem & { command: () => void })[] = [];
  private readonly _websiteAutoUpdateDialogVisible = signal(false);
  public get websiteAutoUpdateDialogVisible(): boolean { return this._websiteAutoUpdateDialogVisible(); }
  public set websiteAutoUpdateDialogVisible(value: boolean) { this._websiteAutoUpdateDialogVisible.set(value); }
  public websiteAutoUpdateIntervalInput = '7';
  private readonly _websiteAutoUpdateIntervalError = signal('');
  public get websiteAutoUpdateIntervalError(): string { return this._websiteAutoUpdateIntervalError(); }
  public set websiteAutoUpdateIntervalError(value: string) { this._websiteAutoUpdateIntervalError.set(value); }
  private websiteAutoUpdateDialogDoc: SelectableDocument | null = null;

  private readonly _extractionDialogVisible = signal(false);
  public get extractionDialogVisible(): boolean { return this._extractionDialogVisible(); }
  public set extractionDialogVisible(value: boolean) { this._extractionDialogVisible.set(value); }
  private readonly _extractionDialogDoc = signal<SelectableDocument | null>(null);
  public get extractionDialogDoc(): SelectableDocument | null { return this._extractionDialogDoc(); }
  public set extractionDialogDoc(value: SelectableDocument | null) { this._extractionDialogDoc.set(value); }
  private readonly _detailDrawer = signal(false);
  public get detailDrawer(): boolean { return this._detailDrawer(); }
  public set detailDrawer(value: boolean) { this._detailDrawer.set(value); }
  private readonly _selectedDoc = signal<SelectableDocument | null>(null);
  public get selectedDoc(): SelectableDocument | null { return this._selectedDoc(); }
  public set selectedDoc(value: SelectableDocument | null) { this._selectedDoc.set(value); }
  private readonly _selectedDocChunks = signal<DocumentChunk[]>([]);
  public get selectedDocChunks(): DocumentChunk[] { return this._selectedDocChunks(); }
  public set selectedDocChunks(value: DocumentChunk[]) { this._selectedDocChunks.set(value); }
  private readonly _chunksLoading = signal(false);
  public get chunksLoading(): boolean { return this._chunksLoading(); }
  public set chunksLoading(value: boolean) { this._chunksLoading.set(value); }


  constructor(
    public projectDocumentsService: ProjectDocumentsService,
    public projectService: ProjectService,
    private confirmationService: ConfirmationService,
    protected fileUploadService: FileUploadService,
    protected sharePointService: SharePointService,
    private toastr: ToastrService
  ) { }

  public ngOnInit(): void {
    if (this.projectService.selectedProject) {
      this.projectDocumentsService.selectedProjectId = this.projectService.selectedProject._id;
    } else {
      this.projectDocumentsService.selectedProjectId = "";
    }
    this.projectDocuments = this.projectDocumentsService.projectDocuments.value;

    this.projectDocumentsService.projectDocuments.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(docs => {
      this.projectDocuments = docs;
      const ids = new Set(docs.map(doc => doc._id));
      this.selectedDocuments = this.selectedDocuments.filter(doc => ids.has(doc._id));
    });
    void this.fileUploadService.loadAllowedTypes();
  }

  public async loadDocumentsPage(event: TableLazyLoadEvent): Promise<void> {
    this.selectedDocuments = [];
    const search: unknown = event.globalFilter;
    await this.projectDocumentsService.loadPage({
      offset: event.first ?? 0,
      limit: event.rows ?? 25,
      sortField: event.sortField === 'filename' ? 'filename' : 'uploadDate',
      sortOrder: event.sortOrder === 1 ? 1 : -1,
      search: typeof search === 'string' ? search : ''
    });
  }

  public getNewBtnLabel(uploading: boolean | undefined, progress: number) {
    if (uploading) {
      if (progress < 100) {
        return $localize`:@@uploading:Uploading ${progress}%`;
      }
      else {
        return $localize`:@@processing:Verarbeiten...`;
      }
    }
    return $localize`:@@upload:Hochladen`;
  }

  //spannend
  public uploadError(event: FileUploadErrorEvent) {
    const httpErr = (event.error as { error?: { errorType?: string; message?: string } } | undefined);
    const errorType: string | undefined = httpErr?.error?.errorType;
    const serverMessage: string | undefined = httpErr?.error?.message;
    const hint: string = errorType === 'ParsingError'
      ? $localize`:@@upload-error-parsing:Die Datei konnte nicht verarbeitet werden. Prüfe, ob das Format unterstützt wird.`
      : serverMessage ?? $localize`:@@upload-doc-error:Das Hochladen ist fehlgeschlagen. Versuche es erneut.`;
    const body = this.buildToastBody(hint, errorType ? serverMessage : undefined);
    this.toastr.error("GENERAL", body, { enableHtml: true });
  }

  private buildToastBody(hint: string, rawMessage?: string): string {
    if (!rawMessage) return hint;
    const escaped = rawMessage.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `${hint}<br><small class="opacity-75 d-block mt-1">${escaped}</small>`;
  }

  public openMenu(event: MouseEvent, popover: Popover, doc: SelectableDocument, button: Button) {
    if (this.activePop && this.activePop !== popover) {
      this.activePop.hide();
    }
    this.contextMenu = this.getMenuItems(doc);
    popover.toggle(event, button.el.nativeElement);
    this.activePop = popover;
  }

  private getMenuItems(doc: SelectableDocument) {
    const menu: (MenuItem & { command: () => void })[] = [];
    menu.push(
      {
        label: $localize`:@@replace-document:Dokument ersetzen`,
        icon: 'ri-upload-line',
        command: () => {
          const inputEl = document.querySelector("#fileReplace-" + doc._id + " input");
          if (inputEl instanceof HTMLElement) {
            inputEl.click();
          }
        }
      }
    );

    if (this.sharePointService.isSharepointAvailable) {
      menu.push({
        label: $localize`:@@replace-from-sharepoint:Aus SharePoint ersetzen`,
        icon: 'ri ri-microsoft-line',
        command: () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          (this.sharepointBtn as any)?.openForReplace(doc._id);
        }
      });
    }
    if (this.isSharePointSource(doc)) {
      menu.push({
        label: doc.autoUpdate?.enabled
          ? $localize`:@@disable-sharepoint-auto-update:SharePoint Auto-Update deaktivieren`
          : $localize`:@@enable-sharepoint-auto-update:SharePoint Auto-Update aktivieren`,
        icon: doc.autoUpdate?.enabled ? 'ri-pause-circle-line' : 'ri-refresh-line',
        command: () => {
          void this.projectDocumentsService.setDocumentAutoUpdate(doc._id, !doc.autoUpdate?.enabled);
        }
      });
    }
    if (this.isWebsiteSource(doc)) {
      menu.push({
        label: $localize`:@@website-reload-now:Webseite jetzt neu laden`,
        icon: 'ri-refresh-line',
        command: () => {
          void this.projectDocumentsService.reloadWebsiteDocument(doc._id);
        }
      });
      menu.push({
        label: doc.autoUpdate?.enabled
          ? $localize`:@@disable-website-auto-update:Automatisches Neuladen deaktivieren`
          : $localize`:@@enable-website-auto-update:Automatisches Neuladen aktivieren`,
        icon: doc.autoUpdate?.enabled ? 'ri-pause-circle-line' : 'ri-time-line',
        command: () => {
          if (doc.autoUpdate?.enabled) {
            void this.projectDocumentsService.updateWebsiteAutoUpdateConfig(doc._id, false);
          } else {
            this.openWebsiteAutoUpdateDialog(doc);
          }
        }
      });
    }
    menu.push({
      label: $localize`:@@replace-from-website:Aus Webseiten ersetzen`,
      icon: 'ri ri-microsoft-line',
      command: () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        (this.websiteBtn as any)?.openForReplace(doc._id);
      }
    });
    if (!doc.mimetype.startsWith('image/')) {
      menu.push({
        label: $localize`:@@extraction-configure:Extraktion konfigurieren`,
        icon: 'ri-settings-line',
        command: () => {
          this.openExtractionDialog(doc);
        }
      });
    }
    menu.push(
      {
        label: $localize`:@@delete:Löschen`,
        icon: 'ri ri-delete-bin-line',
        command: () => {
          this.confirmationCheck(doc._id);
        }
      }
    );
    return menu;
  }

  private isSharePointSource(doc: SelectableDocument) {
    return doc.source?.type === 'sharepoint-file' || doc.source?.type === 'sharepoint-page';
  }

  private isWebsiteSource(doc: SelectableDocument) {
    return doc.source?.type === 'website';
  }

  private openWebsiteAutoUpdateDialog(doc: SelectableDocument) {
    this.websiteAutoUpdateDialogDoc = doc;
    this.websiteAutoUpdateIntervalInput = doc.autoUpdate?.intervalDays?.toString() ?? '7';
    this.websiteAutoUpdateIntervalError = '';
    this.websiteAutoUpdateDialogVisible = true;
  }

  public closeWebsiteAutoUpdateDialog() {
    this.websiteAutoUpdateDialogVisible = false;
    this.websiteAutoUpdateDialogDoc = null;
    this.websiteAutoUpdateIntervalError = '';
  }

  public saveWebsiteAutoUpdateConfig() {
    if (!this.websiteAutoUpdateDialogDoc) {
      return;
    }

    const days = parseInt(this.websiteAutoUpdateIntervalInput, 10);
    if (!(days >= 1 && days <= 365)) {
      this.websiteAutoUpdateIntervalError = $localize`:@@website-interval-invalid:Das Intervall muss zwischen 1 und 365 Tagen liegen.`;
      return;
    }

    const urls = (this.websiteAutoUpdateDialogDoc.source?.urls ?? [this.websiteAutoUpdateDialogDoc.source?.url])
      .filter((url): url is string => !!url);

    void this.projectDocumentsService.updateWebsiteAutoUpdateConfig(
      this.websiteAutoUpdateDialogDoc._id,
      true,
      days,
      urls
    );

    this.closeWebsiteAutoUpdateDialog();
  }

  public getMimeTypeIcon(mimetype: string): string {
    if (!mimetype) return 'ri-file-line';
    if (mimetype === 'application/pdf') return 'ri-file-pdf-2-line';
    if (mimetype.startsWith('image/')) return 'ri-image-line';
    if (mimetype.startsWith('audio/')) return 'ri-file-music-line';
    if (mimetype.startsWith('video/')) return 'ri-video-line';
    if (
      mimetype === 'application/msword' ||
      mimetype === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) return 'ri-file-word-2-line';
    if (
      mimetype === 'application/vnd.ms-excel' ||
      mimetype === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) return 'ri-file-excel-2-line';
    if (
      mimetype === 'application/vnd.ms-powerpoint' ||
      mimetype === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ) return 'ri-file-ppt-2-line';
    if (mimetype === 'text/plain' || mimetype === 'text/csv') return 'ri-file-text-line';
    return 'ri-file-line';
  }

  public openExtractionDialog(doc: SelectableDocument): void {
    if (doc.extractionStatus === 'extracting') {
      this.toastr.error('GENERAL', $localize`:@@extraction-already-running:Die Extraktion läuft bereits. Bitte warte, bis sie abgeschlossen ist.`);
      return;
    }
    this.extractionDialogDoc = doc;
    this.extractionDialogVisible = true;
  }

  public onExtractionDone(): void {
    void this.projectDocumentsService.reloadDocuments();
  }

  public getExtractionErrorTypeHint(errorType?: string): string {
    switch (errorType) {
      case 'ParsingError':
        return $localize`:@@extraction-error-parsing:Das Dokument konnte nicht gelesen werden. Prüfe, ob das Format korrekt ist.`;
      case 'InternalError':
        return $localize`:@@extraction-error-internal:Ein interner Fehler ist aufgetreten. Versuche es erneut.`;
      case 'ExtractionError':
        return $localize`:@@extraction-error-extraction:Die Inhalte konnten nicht extrahiert werden. Prüfe das Dokument und versuche es erneut.`;
      default:
        return '';
    }
  }

  public confirmationCheck(docId: string) {
    this.confirmationService.confirm({
      header: $localize`:@@confirm-delete-doc:Dokument löschen`,
      icon: 'ri-error-warning-line',
      message: $localize`:@@confirm-delete-message-doc:Möchtest du dieses Dokument wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@delete-accept:Löschen`,
        severity: 'danger'
      },
      accept: async () => {
        await this.projectDocumentsService.deleteFile(docId);
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  public confirmBulkDelete(): void {
    if (this.bulkBusy || !this.selectedDocuments.length) return;
    const toDelete = [...this.selectedDocuments];
    const count = toDelete.length;
    this.confirmationService.confirm({
      header: $localize`:@@bulk-delete-docs-header:${count}:count: Dokument(e) löschen?`,
      icon: 'ri-error-warning-line',
      message: $localize`:@@bulk-delete-docs-message:Möchtest du ${count}:count: Dokument(e) wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@delete-accept:Löschen`,
        severity: 'danger'
      },
      accept: async () => {
        if (this.bulkBusy) return;
        this.bulkBusy = true;
        this.bulkFailedNames = '';
        try {
          const result = await runBotBatch(toDelete, async id => {
            if (await this.projectDocumentsService.deleteFile(id, false) === false) {
              throw new Error('delete failed');
            }
          });
          await this.projectDocumentsService.reloadDocuments();
          this.selectedDocuments = result.failed;
          this.bulkFailedNames = result.failed.map(doc => doc.filename).join(', ');
        } finally {
          this.bulkBusy = false;
        }
      }
    });
  }

  public async openDocumentDetail(doc: SelectableDocument): Promise<void> {
    this.selectedDoc = doc;
    this.detailDrawer = true;
    this.chunksLoading = true;
    this.selectedDocChunks = [];
    try {
      this.selectedDocChunks = await this.projectDocumentsService.getProjectDocChunks(doc.docSearchId);
    } finally {
      this.chunksLoading = false;
    }
  }
}
